/** Anything with a readable/writable scroll offset — the host adapter's `ScrollTop`. */
export interface AGUScrollHost {
    ScrollTop: number;
}

/** When to apply a restored offset: after the incoming tab's content has rendered. */
export type ScrollRestoreScheduler = (restore: () => void) => void;

/**
 * Default: the frame after change detection has painted the new tab (a click
 * handler's CD runs before the next frame); synchronous where there is no
 * animation frame (jsdom).
 */
const DefaultScheduler: ScrollRestoreScheduler = (restore) => {
    if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(() => restore());
    } else {
        restore();
    }
};

/**
 * Per-tab scroll memory for a finder's All ↔ Recommended strip (designer
 * checklist 2026-10-24, Grant Finder: "user should stay in the same position
 * when switching between states").
 *
 * Switching tabs re-renders the results column; the scrollport clamps its offset
 * to the new, often shorter, content and the member lands somewhere else when
 * they come back. `Switch` remembers the outgoing tab's offset and, when the
 * incoming tab has been visited, restores that tab's own offset once it has
 * rendered. A first visit keeps the current offset (the browser clamps it if the
 * new content is shorter — the one case the place can move, and it moves back
 * on return). `Clear` on a new query: a fresh result set has no "place".
 */
export class FinderTabScrollMemory {
    private readonly offsets = new Map<string, number>();

    constructor(
        private readonly host: AGUScrollHost,
        private readonly schedule: ScrollRestoreScheduler = DefaultScheduler
    ) {}

    Switch(fromTab: string, toTab: string): void {
        if (fromTab === toTab) return;
        this.offsets.set(fromTab, this.host.ScrollTop);
        const remembered = this.offsets.get(toTab);
        if (remembered === undefined) return;
        this.schedule(() => {
            this.host.ScrollTop = remembered;
        });
    }

    Clear(): void {
        this.offsets.clear();
    }
}
